'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import SectionHeading from '@/components/ui/SectionHeading';
import Card from '@/components/ui/Card';
import DaylightClock from '@/components/ui/DaylightClock';
import {
  Key,
  Package,
  Wifi,
  Flame,
  Droplets,
  Trash2,
  Droplet,
  Plug,
  Phone,
  AlertTriangle,
  Trees,
  Banknote,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Icons are fixed by position; the copy for each section comes from the
// guest-guide manifest (passed down from the server page).
const icons: LucideIcon[] = [
  Key,
  Package,
  Wifi,
  Flame,
  Droplets,
  Trash2,
  Droplet,
  Plug,
  Phone,
  AlertTriangle,
  Trees,
  Banknote,
];

interface GuideSectionText {
  title: string;
  content: string;
}

interface GuideSection extends GuideSectionText {
  icon: LucideIcon;
}

function GuideSectionCard({ section }: { section: GuideSection }) {
  const [expanded, setExpanded] = useState(false);

  const firstLine = section.content.split('. ')[0] + '.';
  const Icon = section.icon;

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-200',
        expanded ? 'ring-1 ring-[#9B7B3A]/30' : ''
      )}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 shrink-0">
              <Icon className="h-5 w-5 text-[#9B7B3A]" />
            </div>
            <div className="min-w-0">
              <h2
                className="font-light text-base text-[#1A1A2E]"
              >
                {section.title}
              </h2>
              <p
                className="mt-1 text-sm leading-relaxed text-[#5A5A6E]"
              >
                {expanded ? section.content : firstLine}
              </p>
            </div>
          </div>
          <div
            className="shrink-0 mt-1 text-[#5A5A6E]"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </div>
      </button>
    </Card>
  );
}

export default function GuestGuidePage({
  heading,
  sections,
  insurance,
  daylight,
}: {
  heading: { title: string; subtitle: string };
  sections: GuideSectionText[];
  insurance: { title: string; body: string };
  daylight?: {
    title?: string;
    polarNightLabel?: string;
    polarNightSub?: string;
    midnightSunLabel?: string;
    midnightSunSub?: string;
    daylightSuffix?: string;
    sunriseLabel?: string;
    sunsetLabel?: string;
  };
}) {
  const guideSections: GuideSection[] = sections.map((s, i) => ({
    ...s,
    icon: icons[i],
  }));

  return (
    <main
      className="min-h-screen pb-16 px-4 bg-[#FAFAF5]"
    >
      <div className="mx-auto max-w-3xl">
        <SectionHeading
          title={heading.title}
          subtitle={heading.subtitle}
          as="h1"
        />

        <div className="mt-8 mb-10">
          <DaylightClock {...daylight} />
        </div>

        <div className="space-y-3">
          {guideSections.map((section) => (
            <GuideSectionCard key={section.title} section={section} />
          ))}
        </div>

        <Card className="mt-10">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="shrink-0 mt-0.5">
                <AlertTriangle className="h-6 w-6 text-[#9B7B3A]" />
              </div>
              <div>
                <h2
                  className="font-light text-lg text-[#1A1A2E]"
                >
                  {insurance.title}
                </h2>
                <p
                  className="mt-2 text-sm leading-relaxed text-[#5A5A6E]"
                >
                  {insurance.body}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </main>
  );
}
